/**
 * CLE-2323 / CLE-2331 — pure helpers for the rate-tools sub-tabs. Extracted into their
 * own file so they can be unit-tested without driving PrimeReact widgets (`FileUpload`
 * customUpload flow + Dropdown internals are awkward to drive through jsdom).
 */

export interface ISplitStats {
  fileMode: string;
  inputRows: number;
  facilityRows: number;
  nonFacilityRows: number;
  distinctCpts: number;
  droppedMissingCpt: number;
  droppedNoRate: number;
  /**
   * CLE-2464 — of the service lines the analyst requested for the non-facility TC override,
   * the subset that actually matched at least one input row (original-cased). Requests not
   * in this list are likely typos that had no effect.
   */
  tcServiceLinesMatched: string[];
  /** CLE-2464 — count of distinct non-fac CPTs whose rate came from a TC row via the override. */
  tcOverriddenCpts: number;
}

export interface IExpandStats {
  inputRows: number;
  /**
   * Rows that received a real rules-sheet (or synonym-resolved) multiplier. Read
   * from `x-expand-mapped-rows` if present, falling back to `x-expand-expanded-rows`
   * (the older name for the same count) for back-compat during the rollout.
   */
  mappedRows: number;
  /**
   * Deprecated — same value as `mappedRows`. Kept on the type until any UI callsite
   * is fully off it. Remove in a future cleanup.
   */
  expandedRows: number;
  /**
   * Rows whose service line had no rules-sheet match. Per CLE-2323 follow-up r2,
   * these still appear in the output CSV with the original label, multiplier=1,
   * and rate=baseRate, with an `Unmapped=true` column for analyst review.
   */
  unmappedRows: number;
  droppedMissingCpt: number;
  droppedNoBaseRate: number;
  unmappedServiceLines: string[];
  /** Rows whose Service Line came from the per-payer library (CLE-2323 follow-on). */
  resolvedFromLibrary: number;
}

/** Read the `x-split-*` diagnostic headers the backend sets on a successful split. */
export function parseSplitStats(headers: Record<string, string>): ISplitStats {
  const num = (k: string) => Number(headers[k] ?? 0);
  let tcMatched: string[] = [];
  try {
    const raw = headers["x-split-tc-service-lines-matched"];
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed))
        tcMatched = parsed.filter((s): s is string => typeof s === "string");
    }
  } catch (_e) {
    /* malformed header — leave empty */
  }
  return {
    fileMode: headers["x-split-file-mode"] ?? "",
    inputRows: num("x-split-input-rows"),
    facilityRows: num("x-split-facility-rows"),
    nonFacilityRows: num("x-split-nonfacility-rows"),
    distinctCpts: num("x-split-distinct-cpts"),
    droppedMissingCpt: num("x-split-dropped-missing-cpt"),
    droppedNoRate: num("x-split-dropped-no-rate"),
    tcServiceLinesMatched: tcMatched,
    tcOverriddenCpts: num("x-split-tc-overridden-cpts"),
  };
}

/**
 * Read the `x-expand-*` diagnostic headers from a successful expand. The unmapped
 * service-line list is JSON-encoded; we defensively swallow parse errors so a bad
 * header doesn't crash the UI.
 */
export function parseExpandStats(
  headers: Record<string, string>
): IExpandStats {
  const num = (k: string) => Number(headers[k] ?? 0);
  let unmappedSls: string[] = [];
  try {
    const raw = headers["x-expand-unmapped-service-lines"];
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed))
        unmappedSls = parsed.filter((s): s is string => typeof s === "string");
    }
  } catch (_e) {
    /* malformed header — leave empty */
  }
  // Prefer the new `x-expand-mapped-rows` header; fall back to the deprecated
  // `x-expand-expanded-rows` for back-compat during the CLE-2323 r2 rollout.
  const mapped =
    headers["x-expand-mapped-rows"] !== undefined
      ? num("x-expand-mapped-rows")
      : num("x-expand-expanded-rows");
  return {
    inputRows: num("x-expand-input-rows"),
    mappedRows: mapped,
    expandedRows: mapped,
    unmappedRows: num("x-expand-unmapped-rows"),
    droppedMissingCpt: num("x-expand-dropped-missing-cpt"),
    droppedNoBaseRate: num("x-expand-dropped-no-base-rate"),
    unmappedServiceLines: unmappedSls,
    resolvedFromLibrary: num("x-expand-resolved-from-library"),
  };
}

/** Friendly label for the splitter's file-mode banner. */
export function fileModeLabel(fileMode: string): string {
  if (fileMode === "TYPE_A_BALANCED")
    return "Detected as balanced (Aetna-style)";
  if (fileMode === "TYPE_B_FACILITY_AS_EXCEPTION")
    return "Detected as facility-as-exception (BCBS-style)";
  return `Detected as ${fileMode || "unknown"}`;
}
